/*
 * Copyright (c) 2025 Amazon.com, Inc. or its affiliates. All rights reserved.
 *
 * PROPRIETARY/CONFIDENTIAL. USE IS SUBJECT TO LICENSE TERMS.
 */

import React from 'react';
import {STAGE, REGION} from 'src/utils/environment';
import * as styles from '../styles/DevErrorDisplay.module.scss';

interface DevErrorDisplayProps {
  error: Error;
  errorInfo?: React.ErrorInfo;
  onDismiss?: () => void;
}

export const DevErrorDisplay: React.FC<DevErrorDisplayProps> = ({
  error,
  errorInfo,
  onDismiss,
}) => {
  const errorDetails = {
    message: error.message,
    stack: error.stack,
    componentStack: errorInfo?.componentStack,
    timestamp: new Date().toISOString(),
    stage: STAGE,
    region: REGION,
    userAgent: navigator.userAgent,
  };

  const copyToClipboard = () => {
    const errorText = `
Error Details:
Message: ${errorDetails.message}
Timestamp: ${errorDetails.timestamp}
Stage: ${errorDetails.stage}
Region: ${errorDetails.region}
User Agent: ${errorDetails.userAgent}

Stack Trace:
${errorDetails.stack}

Component Stack:
${errorDetails.componentStack || 'Not available'}
`.trim();

    navigator.clipboard.writeText(errorText);
  };

  return (
    <div className={styles.container} data-testid="dev-error-display">
      <div className={styles.header}>
        <h2 className={styles.title}>⚠️ Development Error ({STAGE})</h2>
        {onDismiss && (
          <button 
            className={styles.dismissButton}
            onClick={onDismiss}
            aria-label="Dismiss error"
          >
            ✕
          </button>
        )}
      </div>

      <div className={styles.content}>
        <div className={styles.errorMessage}>
          <strong>Error:</strong> {errorDetails.message}
        </div>

        <div className={styles.metadata}>
          <div><strong>Stage:</strong> {errorDetails.stage}</div>
          <div><strong>Region:</strong> {errorDetails.region}</div>
          <div><strong>Timestamp:</strong> {errorDetails.timestamp}</div>
        </div>

        <div className={styles.actions}>
          <button 
            className={styles.copyButton}
            onClick={copyToClipboard}
          >
            Copy Error Details
          </button>
        </div>

        {errorDetails.stack && (
          <details className={styles.stackTrace}>
            <summary>Stack Trace</summary>
            <pre className={styles.stackContent}>{errorDetails.stack}</pre>
          </details>
        )}

        {errorDetails.componentStack && (
          <details className={styles.componentStack}>
            <summary>Component Stack</summary>
            <pre className={styles.stackContent}>{errorDetails.componentStack}</pre>
          </details>
        )}
      </div>
    </div>
  );
};
